/**
 * STORY-036: Interface resume admin
 * Vue des reponses client cote admin avec resume technique genere par IA
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  ChevronLeft,
  Copy,
  FileText,
  Lightbulb,
  MessageSquare,
  Sparkles,
  Target,
  TrendingUp,
  User,
  Zap,
} from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useAIConversation, useGenerateSummary } from '@/hooks/useAI'
import {
  PROJECT_TYPE_LABELS,
  CHANNEL_LABELS,
  type BMADResponse,
} from '@/types/ai'
import { cn } from '@/lib/utils'

// BMAD Category labels and icons
const BMAD_CATEGORIES: Array<{
  key: keyof BMADResponse
  label: string
  icon: typeof User
  color: string
}> = [
  { key: 'background', label: 'Contexte', icon: User, color: 'mint' },
  { key: 'motivation', label: 'Motivation', icon: Target, color: 'gold' },
  { key: 'aspirations', label: 'Aspirations', icon: TrendingUp, color: 'blue' },
  { key: 'decisionMaking', label: 'Decision', icon: Zap, color: 'brown' },
]

export default function AIQuestionnairePage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'responses' | 'summary' | 'prompts'>('responses')
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null)

  const { data: conversation, isLoading } = useAIConversation(conversationId)
  const generateSummary = useGenerateSummary()

  const handleGenerateSummary = () => {
    if (conversationId) {
      generateSummary.mutate(conversationId)
    }
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedPrompt(id)
    setTimeout(() => setCopiedPrompt(null), 2000)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 mx-auto text-beige-300 mb-4" />
        <p className="text-brown-600">Conversation non trouvee</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/ai/conversations')}>
          Retour aux conversations
        </Button>
      </div>
    )
  }

  const summary = conversation.summary
  const bmadResponses = conversation.bmadResponses || {}

  const breadcrumbItems = [
    { label: 'Conversations IA', href: '/ai/conversations' },
    { label: 'Questionnaire' },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold text-brown-900">
              Analyse du questionnaire
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-brown-600">{conversation.clientName || conversation.clientEmail}</span>
              <Badge variant="mint">{CHANNEL_LABELS[conversation.channel]}</Badge>
              {conversation.projectType && (
                <Badge variant="gold">{PROJECT_TYPE_LABELS[conversation.projectType]}</Badge>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={handleGenerateSummary}
          disabled={generateSummary.isPending}
        >
          {generateSummary.isPending ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Generation en cours...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              {summary ? 'Regenerer le resume' : 'Generer le resume IA'}
            </>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-beige-200">
        <nav className="flex gap-4" aria-label="Tabs">
          {[
            { id: 'responses', label: 'Reponses BMAD', icon: FileText },
            { id: 'summary', label: 'Resume technique', icon: Brain },
            { id: 'prompts', label: 'Prompts suggeres', icon: Lightbulb },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-mint-600 text-mint-700'
                  : 'border-transparent text-brown-500 hover:text-brown-700 hover:border-beige-300'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'responses' && (
          <motion.div
            key="responses"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {BMAD_CATEGORIES.map((category) => (
              <Card key={category.key} variant="bordered">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className={cn('p-2 rounded-lg', `bg-${category.color}-50`)}>
                      <category.icon className={cn('w-5 h-5', `text-${category.color}-600`)} />
                    </div>
                    <CardTitle>{category.label}</CardTitle>
                  </div>
                </CardHeader>
                <div className="space-y-3">
                  {bmadResponses[category.key]?.length ? (
                    bmadResponses[category.key]?.map((response, index) => (
                      <div
                        key={index}
                        className="p-3 bg-beige-50 rounded-lg text-sm text-brown-700"
                      >
                        {response}
                      </div>
                    ))
                  ) : (
                    <p className="text-brown-400 text-sm italic">Aucune reponse collectee</p>
                  )}
                </div>
              </Card>
            ))}

            {/* Conversation history */}
            <Card variant="bordered" className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-brown-600" />
                  <CardTitle>Historique de la conversation</CardTitle>
                  <Badge variant="gray">{conversation.messages.length} messages</Badge>
                </div>
              </CardHeader>
              <div className="max-h-96 overflow-y-auto space-y-3">
                {conversation.messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'p-3 rounded-lg text-sm',
                      message.role === 'user'
                        ? 'bg-beige-100 ml-8'
                        : message.role === 'assistant'
                        ? 'bg-mint-50 mr-8'
                        : 'bg-gold-50 text-center'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1 text-xs text-brown-500">
                      {message.role === 'user' ? (
                        <>
                          <User className="w-3 h-3" />
                          Client
                        </>
                      ) : message.role === 'assistant' ? (
                        <>
                          <Brain className="w-3 h-3" />
                          IA
                        </>
                      ) : (
                        'Systeme'
                      )}
                      <span>{new Date(message.timestamp).toLocaleTimeString('fr-FR')}</span>
                    </div>
                    <p className="text-brown-700">{message.content}</p>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {activeTab === 'summary' && (
          <motion.div
            key="summary"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {summary ? (
              <>
                {/* Technical Summary */}
                <Card variant="bordered">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-mint-600" />
                      <CardTitle>Resume technique</CardTitle>
                      <Badge variant="mint">
                        Score de confiance: {Math.round((summary.confidenceScore || 0) * 100)}%
                      </Badge>
                    </div>
                  </CardHeader>
                  <p className="text-brown-700">{summary.technicalSummary}</p>
                </Card>

                {/* Client needs and solutions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card variant="bordered">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-gold-600" />
                        <CardTitle>Besoins identifies</CardTitle>
                      </div>
                    </CardHeader>
                    <ul className="space-y-2">
                      {summary.clientNeeds?.map((need, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-brown-700">
                          <CheckCircle2 className="w-4 h-4 text-mint-600 mt-0.5 flex-shrink-0" />
                          {need}
                        </li>
                      ))}
                    </ul>
                  </Card>

                  <Card variant="bordered">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-gold-600" />
                        <CardTitle>Solutions recommandees</CardTitle>
                      </div>
                    </CardHeader>
                    <ul className="space-y-2">
                      {summary.suggestedSolutions?.map((solution, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-brown-700">
                          <ArrowRight className="w-4 h-4 text-mint-600 mt-0.5 flex-shrink-0" />
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </div>

                {/* Budget and timeline */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {summary.estimatedBudgetRange && (
                    <Card variant="bordered">
                      <CardHeader>
                        <CardTitle>Budget estime</CardTitle>
                      </CardHeader>
                      <p className="text-2xl font-bold text-brown-900">
                        {summary.estimatedBudgetRange.min.toLocaleString('fr-FR')} -{' '}
                        {summary.estimatedBudgetRange.max.toLocaleString('fr-FR')}{' '}
                        {summary.estimatedBudgetRange.currency}
                      </p>
                    </Card>
                  )}

                  {summary.estimatedTimeline && (
                    <Card variant="bordered">
                      <CardHeader>
                        <CardTitle>Delai estime</CardTitle>
                      </CardHeader>
                      <p className="text-2xl font-bold text-brown-900">
                        {summary.estimatedTimeline.min} - {summary.estimatedTimeline.max}{' '}
                        {summary.estimatedTimeline.unit === 'days'
                          ? 'jours'
                          : summary.estimatedTimeline.unit === 'weeks'
                          ? 'semaines'
                          : 'mois'}
                      </p>
                    </Card>
                  )}
                </div>

                {/* BMAD Analysis */}
                <Card variant="bordered">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-mint-600" />
                      <CardTitle>Analyse BMAD</CardTitle>
                    </div>
                  </CardHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {BMAD_CATEGORIES.map((category) => (
                      <div key={category.key} className="p-4 bg-beige-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <category.icon className={cn('w-4 h-4', `text-${category.color}-600`)} />
                          <h4 className="font-medium text-brown-900">{category.label}</h4>
                        </div>
                        <p className="text-sm text-brown-600">
                          {summary.bmadAnalysis?.[category.key] || 'Analyse non disponible'}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            ) : (
              <Card variant="bordered" className="text-center py-12">
                <Brain className="w-12 h-12 mx-auto text-beige-300 mb-4" />
                <h3 className="text-lg font-semibold text-brown-900 mb-2">
                  Aucun resume genere
                </h3>
                <p className="text-brown-500 mb-4">
                  Generez un resume IA pour obtenir une analyse technique du projet
                </p>
                <Button onClick={handleGenerateSummary} disabled={generateSummary.isPending}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generer le resume
                </Button>
              </Card>
            )}
          </motion.div>
        )}

        {activeTab === 'prompts' && (
          <motion.div
            key="prompts"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <Card variant="bordered">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-gold-600" />
                  <CardTitle>Prompts BMAD suggeres</CardTitle>
                </div>
              </CardHeader>
              <p className="text-sm text-brown-500 mb-4">
                Utilisez ces prompts pour approfondir la discussion avec le client
              </p>

              {summary?.suggestedPrompts?.length ? (
                <div className="space-y-3">
                  {summary.suggestedPrompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between gap-4 p-4 bg-beige-50 rounded-lg group"
                    >
                      <p className="text-sm text-brown-700">{prompt}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(prompt, `prompt-${index}`)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {copiedPrompt === `prompt-${index}` ? (
                          <CheckCircle2 className="w-4 h-4 text-mint-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Lightbulb className="w-12 h-12 mx-auto text-beige-300 mb-4" />
                  <p className="text-brown-500">
                    Generez un resume pour obtenir des prompts de suivi
                  </p>
                </div>
              )}
            </Card>

            {/* Standard BMAD prompts */}
            <Card variant="bordered">
              <CardHeader>
                <CardTitle>Prompts BMAD standards</CardTitle>
              </CardHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    category: 'Background',
                    prompts: [
                      'Parlez-moi de votre entreprise et de son histoire',
                      'Qui sont vos principaux concurrents ?',
                      'Quelle est votre situation actuelle en matiere de presence digitale ?',
                    ],
                  },
                  {
                    category: 'Motivation',
                    prompts: [
                      'Qu\'est-ce qui vous pousse a entreprendre ce projet maintenant ?',
                      'Quels problemes souhaitez-vous resoudre ?',
                      'Quelles opportunites voulez-vous saisir ?',
                    ],
                  },
                  {
                    category: 'Aspirations',
                    prompts: [
                      'Comment imaginez-vous le resultat ideal ?',
                      'Quels KPIs mesureront le succes du projet ?',
                      'Ou voyez-vous votre entreprise dans 2 ans ?',
                    ],
                  },
                  {
                    category: 'Decision',
                    prompts: [
                      'Quel est votre processus de prise de decision ?',
                      'Quels sont vos criteres de choix pour un prestataire ?',
                      'Avez-vous des contraintes de temps ou de budget ?',
                    ],
                  },
                ].map((group) => (
                  <div key={group.category} className="p-4 bg-beige-50 rounded-lg">
                    <h4 className="font-medium text-brown-900 mb-3">{group.category}</h4>
                    <ul className="space-y-2">
                      {group.prompts.map((prompt, index) => (
                        <li
                          key={index}
                          className="flex items-start justify-between gap-2 text-sm text-brown-600 group cursor-pointer hover:text-brown-900"
                          onClick={() => copyToClipboard(prompt, `std-${group.category}-${index}`)}
                        >
                          <span>{prompt}</span>
                          {copiedPrompt === `std-${group.category}-${index}` ? (
                            <CheckCircle2 className="w-4 h-4 text-mint-600 flex-shrink-0" />
                          ) : (
                            <Copy className="w-4 h-4 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
